import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TrafficLightDot } from '@/components/PostureIndicator'
import { Skeleton } from '@/components/ui/skeleton'
import { TRAFFIC } from '@/lib/trafficLight'

const BUCKET_META = [
  { id: 'virus_detected', label: 'Virus detected', status: 'red', colour: '#ef4444' },
  { id: 'no_av', label: 'No AV', status: 'red', colour: '#dc2626' },
  { id: 'av_not_updated', label: 'AV not updated', status: 'amber', colour: '#f59e0b' },
  { id: 'ok', label: 'OK', status: 'green', colour: '#10b981' },
  { id: 'no_data', label: 'No data', status: 'grey', colour: '#94a3b8' },
]

const LABEL_TO_BUCKET = Object.fromEntries(
  BUCKET_META.map((row) => [row.label, row.id]),
)

function overallFromCounts(counts) {
  if (!counts) return 'grey'
  if ((counts.virus_detected || 0) > 0 || (counts.no_av || 0) > 0) return 'red'
  if ((counts.av_not_updated || 0) > 0) return 'amber'
  if ((counts.ok || 0) > 0) return 'green'
  return 'grey'
}

function headline(counts, total) {
  if (!total) return 'No devices in scope'
  const virus = counts?.virus_detected || 0
  const noAv = counts?.no_av || 0
  const stale = counts?.av_not_updated || 0
  const issues = virus + noAv + stale
  if (virus > 0) {
    return `${virus} device${virus === 1 ? '' : 's'} with virus detections`
  }
  if (noAv > 0) {
    return `${noAv} device${noAv === 1 ? '' : 's'} with no antivirus cover`
  }
  if (stale > 0) {
    return `${stale} device${stale === 1 ? '' : 's'} with outdated AV signatures`
  }
  if (issues === 0 && (counts?.ok || 0) > 0) {
    return 'No antivirus issues in assessed devices'
  }
  return 'Antivirus assessment incomplete for some devices'
}

function countsToPieData(counts) {
  return BUCKET_META.map((row) => ({
    label: row.label,
    value: Number(counts?.[row.id] || 0),
    bucket: row.id,
    status: row.status,
    colour: row.colour,
  })).filter((row) => row.value > 0)
}

export function AntivirusEstatePie({
  summary,
  loading,
  selectedFilter = null,
  onSelectFilter,
}) {
  const counts = summary?.counts
  const total = summary?.total_devices ?? 0
  const pieData = useMemo(() => countsToPieData(counts), [counts])
  const overall = overallFromCounts(counts)
  const traffic = TRAFFIC[overall] ?? TRAFFIC.grey
  const activeLabel =
    selectedFilter?.chart === 'antivirus' ? selectedFilter.key : null

  function handleSliceClick(label) {
    const bucket = LABEL_TO_BUCKET[label]
    if (!bucket) return
    onSelectFilter?.({ chart: 'antivirus', key: label, bucket })
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="mt-2 h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[180px] w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <TrafficLightDot status={overall} className="h-4 w-4" />
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">Antivirus</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {headline(counts, total)}
            </p>
          </div>
          {overall !== 'grey' && total > 0 ? (
            <Badge variant={traffic.badge} className="shrink-0">
              {traffic.label}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SimplePieChart
          data={pieData}
          emptyMessage="No antivirus data yet"
          colourFor={(entry) => entry.colour}
          heightClassName="h-[180px]"
          activeLabel={activeLabel}
          onItemClick={onSelectFilter ? handleSliceClick : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Windows devices scored from Defender, inventory AV, and ThreatDown cover.
          Signatures older than {summary?.signature_stale_days ?? 7} days count as not
          updated.{' '}
          {onSelectFilter ? 'Click a slice to list devices. ' : null}
          <Link to="/defender" className="text-primary hover:underline">
            Windows Defender
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
